import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { PwTestIdDirective } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { HEADER_TEST_IDS } from '@core/tellent-assistant-util';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tellent-assistant-header',
  imports: [
    ButtonComponent,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    PwTestIdDirective,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.expanded]': 'expanded()' },
  template: `
    @if (!expanded()) {
      <rt-icon-button
        [pwTestId]="testIds.menu"
        variant="plain"
        iconName="menu"
        size="small"
        (click)="menu.emit()"
      />
    }
    @if (inThread()) {
      <rt-heading variant="subheading" class="title">
        <span class="title-text">{{ title() }}</span>
      </rt-heading>
      <rt-button
        [pwTestId]="testIds.newChat"
        size="small"
        variant="tertiary"
        (click)="newChat.emit()"
      >
        <rt-icon name="edit-square" />
        {{ 'tlc.tellent_assistant.header.new_chat_button' | translate }}
      </rt-button>
    }
    @if (expanded()) {
      <rt-icon-button
        #menuOrigin="rtOverlayOrigin"
        rt-overlay-origin
        [pwTestId]="testIds.more"
        variant="plain"
        iconName="more-vert"
        size="small"
        class="more"
        [class.more-solo]="!inThread()"
        [active]="menuOpen()"
        (click)="menuOpen.set(true)"
      />
      <ng-template
        rt-connected-overlay
        [origin]="menuOrigin"
        [open]="menuOpen()"
        [positions]="menuPositions"
        [offsetY]="4"
        [width]="180"
        hasBackdrop
        withOverlayLevel
        (backdropClick)="menuOpen.set(false)"
      >
        <rt-dropdown>
          <rt-dropdown-item
            [pwTestId]="testIds.menuPin"
            (click)="menuOpen.set(false); pinThread.emit()"
          >
            <rt-icon name="pin" prefix />{{ pinLabel() | translate }}
          </rt-dropdown-item>
          <rt-divider />
          <rt-dropdown-item [pwTestId]="testIds.menuRemove" (click)="menuOpen.set(false)">
            <rt-icon name="delete" prefix />{{
              'tlc.tellent_assistant.header.menu_remove' | translate
            }}
          </rt-dropdown-item>
        </rt-dropdown>
      </ng-template>
    }
    <rt-icon-button
      [pwTestId]="testIds.modeToggle"
      variant="plain"
      [iconName]="toggleIcon()"
      size="small"
      class="mode-toggle"
      [class.mode-toggle-solo]="!inThread() && !expanded()"
      (click)="toggleMode()"
    />
    <rt-icon-button
      [pwTestId]="testIds.close"
      variant="plain"
      iconName="close"
      size="small"
      class="close"
      (click)="close.emit()"
    />
  `,
  styleUrl: './tellent-assistant-header.component.less',
})
export class TellentAssistantHeaderComponent {
  protected readonly testIds = HEADER_TEST_IDS;
  protected readonly toggleIcon = computed(() => (this.expanded() ? 'collapse' : 'open-in-full'));
  protected readonly menuOpen = signal<boolean>(false);
  protected readonly menuPositions = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ] satisfies ConnectionPositionPair[];
  protected readonly pinLabel = computed(() =>
    this.pinned()
      ? 'tlc.tellent_assistant.header.menu_unpin'
      : 'tlc.tellent_assistant.header.menu_pin',
  );

  public readonly title = input.required<string>();
  public readonly inThread = input.required<boolean>();
  public readonly expanded = input.required<boolean>();
  public readonly pinned = input<boolean>(false);

  public readonly menu = output<void>();
  public readonly newChat = output<void>();
  public readonly close = output<void>();
  public readonly expand = output<void>();
  public readonly collapse = output<void>();
  public readonly pinThread = output<void>();

  protected toggleMode(): void {
    this.expanded() ? this.collapse.emit() : this.expand.emit();
  }
}
